// Weighted smooth scrolling: mouse-wheel input glides to a stop with a damped ease-out instead of stepping.
// Uses the page's native scroll position, so sticky sections, scroll-driven animations, anchors and the
// keyboard all keep working. Off on touch screens, trackpad-free devices and for reduced motion.
class SSmoothScroll extends HTMLElement {
  #controller = null;
  #frame = 0;
  #target = 0;
  #current = 0;
  #last = 0;
  #running = false;

  connectedCallback() {
    const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!fine || reduce || this.#controller) return;
    this.#controller = new AbortController();
    const signal = this.#controller.signal;
    const ease = Number(this.getAttribute('ease')) || 0.11;
    this.#current = this.#target = window.scrollY;

    window.addEventListener('wheel', (event) => {
      if (event.ctrlKey || event.defaultPrevented) return;
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
      if (document.querySelector('dialog:modal') || this.#insideScroller(event.target, event.deltaY)) return;

      let delta = event.deltaY;
      if (event.deltaMode === 1) delta *= 16;
      else if (event.deltaMode === 2) delta *= window.innerHeight;

      event.preventDefault();
      if (!this.#running) this.#current = window.scrollY;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      this.#target = Math.max(0, Math.min(max, (this.#running ? this.#target : window.scrollY) + delta));
      this.#start(ease);
    }, { passive: false, signal });

    // Keyboard, scrollbar drags and anchor jumps move the page natively; follow them instead of fighting.
    window.addEventListener('scroll', () => {
      if (!this.#running) this.#current = this.#target = window.scrollY;
    }, { passive: true, signal });
  }

  #start(ease) {
    if (this.#running) return;
    this.#running = true;
    this.#last = performance.now();
    const step = (now) => {
      const dt = Math.min(64, now - this.#last) / (1000 / 60);
      this.#last = now;
      const factor = 1 - Math.pow(1 - ease, dt);
      this.#current += (this.#target - this.#current) * factor;
      if (Math.abs(this.#target - this.#current) < 0.5) this.#current = this.#target;
      window.scrollTo({ top: this.#current, behavior: 'instant' });
      if (this.#current === this.#target) {
        this.#running = false;
        return;
      }
      this.#frame = requestAnimationFrame(step);
    };
    this.#frame = requestAnimationFrame(step);
  }

  // Let inner scrolling areas (drawers, swipe rows, long lists) scroll on their own.
  #insideScroller(node, deltaY) {
    let element = node instanceof Element ? node : null;
    while (element && element !== document.body && element !== document.documentElement) {
      const style = getComputedStyle(element);
      if (/(auto|scroll)/.test(style.overflowY) && element.scrollHeight > element.clientHeight) {
        const atTop = element.scrollTop <= 0;
        const atBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 1;
        if ((deltaY < 0 && !atTop) || (deltaY > 0 && !atBottom)) return true;
      }
      element = element.parentElement;
    }
    return false;
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    cancelAnimationFrame(this.#frame);
    this.#running = false;
  }
}

if (!customElements.get('s-smooth-scroll')) customElements.define('s-smooth-scroll', SSmoothScroll);
